import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { useAddEventToReportMenuItem, useWebSocketStatus } from '@/hooks';
import { getAlertDescription } from '@/utils/detection-classes';
import { alertKey, isAlertNew, useAlertsStore } from '@/stores';
import type { ActionMenuItem } from '@/components';
import type { AlertRuleEvent, Event } from '@/types/event';

interface AlertFeedCardConfig {
    isLive: true;
    isConnected: boolean;
    cardTitle: string;
    itemKey: (alert: AlertRuleEvent) => string;
    itemIsNew: (alert: AlertRuleEvent) => boolean;
    itemEvent: (alert: AlertRuleEvent) => Event;
    itemDescription: (alert: AlertRuleEvent) => string;
    itemConfidence: (alert: AlertRuleEvent) => number | undefined;
    itemCameraName: (alert: AlertRuleEvent, event: Event) => string | undefined;
    itemActionMenuItems: (
        alert: AlertRuleEvent,
        event: Event,
    ) => ActionMenuItem[];
}

export function useAlertFeedCardConfig(): AlertFeedCardConfig {
    const { t } = useTranslation(I18nNamespace.EVENTS);
    const isConnected = useWebSocketStatus();
    const addToReportItem = useAddEventToReportMenuItem();
    const alertArrivals = useAlertsStore((state) => state.alertArrivals);

    return {
        isLive: true,
        isConnected,
        cardTitle: t('detection.alert'),
        itemKey: (alert) => alertKey(alert),
        itemIsNew: (alert) => isAlertNew(alertArrivals, alertKey(alert)),
        itemEvent: (alert) => alert.event,
        itemDescription: (alert) =>
            getAlertDescription(t, alert.promptText, alert.event.classId),
        itemConfidence: (alert) => alert.confidence,
        itemCameraName: (alert, event) => alert.sourceName ?? event.sourceName,
        itemActionMenuItems: (alert, event) =>
            addToReportItem(event, { confidence: alert.confidence }),
    };
}
